/**
 * FileUpload Component
 * Modern drag-and-drop file upload with glassmorphism and preview
 */

import React, { useState, useRef } from "react";
import { cn } from "../../utils";
import {
  validateFileType,
  validateFileSize,
} from "../../utils/validation.utils";
import { UploadCloud, File, X, Image as ImageIcon, Film, Box } from "lucide-react";

interface FileUploadProps {
  accept: string[];
  maxSize: number; // in MB
  onFileSelect: (file: File) => void;
  onError?: (error: string) => void;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  maxSize,
  onFileSelect,
  onError,
  label,
  error,
  helperText,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use external error prop or internal error
  const displayError = error || internalError;

  // Check if file is a 3D model
  const is3DModel = (filename: string): boolean => {
    return /\.(stl|obj|ply|step|stp)$/i.test(filename);
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFile = (file: File) => {
    // Clear previous error
    setInternalError(null);

    // Validate file type
    if (!validateFileType(file, accept)) {
      const errorMsg = `Invalid file type. Accepted: ${accept.join(", ")}`;
      setInternalError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(file, maxSize);
    if (!sizeValidation.isValid) {
      const errorMsg = `File too large. Maximum size: ${maxSize}MB`;
      setInternalError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    // Generate preview based on file type
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      // Revoke previous URL to prevent memory leak
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
      const videoUrl = URL.createObjectURL(file);
      setPreview(videoUrl);
    } else if (is3DModel(file.name)) {
      setPreview("3d-model-file");
    }

    setFileName(file.name);
    setFileSize(formatFileSize(file.size));
    onFileSelect(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const clearFile = () => {
    // Revoke blob URL to prevent memory leak
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setFileName(null);
    setFileSize(null);
    setInternalError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Get file extension for display
  const getFileExtension = (filename: string): string => {
    return filename.substring(filename.lastIndexOf(".")).toUpperCase();
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-300 mb-2">
          {label}
        </label>
      )}

      <div
        className={cn(
          "group relative flex flex-col items-center justify-center w-full transition-all duration-300 ease-in-out",
          "border-2 border-dashed rounded-xl overflow-hidden",
          isDragging
            ? "border-primary-500 bg-primary-500/10 scale-[1.01]"
            : displayError
            ? "border-red-500/50 bg-red-500/5 hover:bg-red-500/10"
            : "border-slate-700 hover:border-primary-500/50 bg-slate-800/30 hover:bg-slate-800/50",
          disabled && "opacity-50 cursor-not-allowed hover:border-slate-700 hover:bg-slate-800/30",
          preview ? "p-0 border-solid border-slate-700" : "py-6 px-4"
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!preview ? handleClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept.join(",")}
          onChange={handleFileInputChange}
          disabled={disabled}
        />

        {preview ? (
          <div className="w-full relative">
            {/* Compact Preview - Horizontal Layout */}
            <div className="flex items-center gap-4 p-4 bg-slate-800/50">
              {/* Thumbnail */}
              <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-black/30 flex items-center justify-center">
                {preview === "3d-model-file" ? (
                  <Box className="w-8 h-8 text-indigo-400" />
                ) : fileName?.match(/\.(mp4|avi|mov|mkv|webm)$/i) ? (
                  <Film className="w-8 h-8 text-cyan-400" />
                ) : (
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              
              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate text-sm">{fileName}</p>
                <p className="text-slate-400 text-xs mt-0.5">
                  {fileSize} • {getFileExtension(fileName || "")}
                </p>
              </div>
              
              {/* Remove Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
                className="flex-shrink-0 p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center transition-transform duration-300 group-hover:scale-[1.02]">
            <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center transition-colors duration-300 ${isDragging ? "bg-primary-500 text-white" : "bg-slate-700/50 text-slate-400 group-hover:bg-primary-500/20 group-hover:text-primary-400"}`}>
              <UploadCloud className="w-6 h-6" />
            </div>
            
            <h3 className="text-base font-semibold text-white mb-0.5">
              {isDragging ? "Drop to Upload" : "Click to Upload"}
            </h3>
            <p className="text-slate-400 text-sm mb-2">
              or drag and drop your file here
            </p>
            
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700 text-xs text-slate-500">
              <File className="w-3 h-3" />
              <span>Max size: {maxSize}MB</span>
            </div>
          </div>
        )}
      </div>

      {displayError && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-lg animate-in slide-in-from-top-1">
          <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{displayError}</span>
        </div>
      )}

      {helperText && !displayError && (
        <p className="mt-2 text-xs text-slate-500 text-center px-4">
           {helperText}
        </p>
      )}
    </div>
  );
};
